// ----------------------------------------------------------------------- imports externos
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

// ----------------------------------------------------------------------- imports internos
import { MINIMO, POR, buscar } from "../../shared/buscar";
import { rutaDePersona } from "../../shared/personas";
import { ruta as rutaDeUnidad } from "../../shared/unidad";
import {
  claseDeEmpresa,
  etiquetaCorta as siglaEmpresa,
  nombreDe as nombreEmpresa,
} from "../../shared/empresa";
import { useRealtime } from "../../services/store";
import { unidadesHistoricas } from "../../services/porteria";
import { buscarGlobal } from "../../services/buscarGlobal";
import usePolizas from "../../hooks/usePolizas";
import { dominioPoliza, lineaSeguro, puedeVerSeguro } from "../../shared/seguroFicha";
import Patente from "../funcionales/Patente";
import { pantallasQueCoinciden } from "./secciones";

/* La caja del hub: se escribe un interno, una patente o un apellido.
 *
 * ── ES LA PUERTA DE LA FICHA ─────────────────────────────────────────────
 *
 * La ficha de la unidad no sirve si para llegar hay que saber de antemano en
 * qué padrón está la unidad y navegar hasta su fila. Sin esto, la ficha es una
 * pantalla más a la que casi nadie llega.
 *
 * ── NO SALE A LA RED ─────────────────────────────────────────────────────
 *
 * Los tres padrones ya están en memoria: el armazón los tiene cargados para
 * los números del menú. Así que buscar cuesta recorrer tres listas y contesta
 * en la misma tecla — sin espera, sin `debounce` y sin un estado de «cargando»
 * que en una caja de búsqueda se lee como que el sistema se colgó.
 *
 * ── Y NUNCA ELIGE POR VOS ────────────────────────────────────────────────
 *
 * 430 internos existen en las dos tablas. Escribir `774` muestra el tractor Y
 * el furgón, con su patente al lado para distinguirlos. El Enter va al primero
 * —el más exacto— y esa preferencia está escrita en `shared/buscar`, no acá:
 * la pantalla dibuja, la regla se prueba sola.
 */

/* Ctrl+K / ⌘K. Es el atajo que la gente ya conoce de otras aplicaciones, y no
 * pisa nada del navegador en Chrome ni en Firefox. */
const esAtajo = (e) => (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k";

/* LA EMPRESA DE CADA RESULTADO (Igna, 02/10: «que aparezca la empresa a la
 * que corresponde, tanto personas como vehículos»). El mismo chip y la misma
 * traducción del CUIT que la ficha: el buscador no puede decir TC de un
 * camión que la ficha dice TA. Sin empresa cargada no se dibuja nada —un «—»
 * en cada renglón sería ruido—; una que no conocemos sale con su CUIT, en
 * gris, como en el resto del hub. */
const Empresa = ({ valor }) => {
  const sigla = siglaEmpresa(valor);
  if (sigla === "—") return null;
  return (
    <span className={claseDeEmpresa(valor)} title={nombreEmpresa(valor) || sigla}>
      {sigla}
    </span>
  );
};

const Buscador = ({ permisos = null }) => {
  const navegar = useNavigate();
  const [texto, setTexto] = useState("");
  const [abierto, setAbierto] = useState(false);
  const caja = useRef(null);
  const entrada = useRef(null);

  const tractores = useRealtime("tractores");
  const furgones = useRealtime("furgones");
  const personas = useRealtime("personas");
  /* EL ARCHIVO DE UNIDADES QUE YA NO SON NUESTRAS (ticket 155 punto 4).
   *
   * Se pide UNA vez y entero: son 99 filas que no cambian nunca —es un archivo,
   * no un padrón— así que consultarlo en cada tecla sería un viaje de red para
   * traer siempre lo mismo. Va con `useState` y no con `useRealtime` porque no
   * hay nada a lo que suscribirse.
   *
   * Si falla, se queda vacío y el buscador sigue andando: no encontrar un
   * camión vendido en 2019 no puede romper la búsqueda de los que trabajan. */
  const [historicas, setHistoricas] = useState([]);
  useEffect(() => {
    let vigente = true;
    unidadesHistoricas().then((h) => vigente && setHistoricas(h));
    return () => {
      vigente = false;
    };
  }, []);

  /* LAS PERSONAS RESPETAN EL PERMISO DE SU PANTALLA, igual que las tarjetas de
   * la home. El DNI y el teléfono de alguien no están abiertos en ninguna otra
   * parte del hub, y su ficha pide `ver.gente`: ofrecerlos acá sería avisar de
   * una puerta y esconder la llave — y peor, mostrar de paso el nombre y el
   * documento a quien no puede abrir el padrón.
   *
   * `null` es «todavía no llegaron» y ahí se muestran, como en todo el resto
   * del hub: parpadear medio segundo se lee como un buscador que no encuentra. */
  const verGente = permisos === null || permisos.includes("ver.gente");

  /* LA PÓLIZA EN EL RESULTADO (Aldo, Tráfico, 23/10, ticket 496: «que se vea
   * ni bien hago la consulta»). Va con `puedeVerSeguro`, el mismo permiso que
   * la línea de la ficha. Se piden TODAS una sola vez, recién la primera vez
   * que se abre la lista: el buscador está montado en todas las pantallas y
   * no tiene por qué leer las pólizas de quien nunca lo usa. Si no se pudo
   * leer, el renglón queda como antes. */
  const [seUso, setSeUso] = useState(false);
  const verPolizas = Array.isArray(permisos) && puedeVerSeguro(permisos);
  const polizas = usePolizas(verPolizas && seUso);

  /* El recorte va ADENTRO del memo: afuera, `verGente ? personas : []` arma un
   * array nuevo en cada render y el memo se recalcula siempre — que es
   * recorrer los tres padrones en cada tecla del resto del hub. */
  const r = useMemo(
    () =>
      buscar(texto, {
        tractores,
        furgones,
        personas: verGente ? personas : [],
        historicas,
      }),
    [texto, tractores, furgones, personas, verGente, historicas],
  );

  /* LA MITAD «A LA RED»: la búsqueda general (vales, siniestros, garantías).
   *
   * Va aparte del padrón a propósito. El padrón contesta en la tecla y no puede
   * esperar; esto sale a la base, así que va con `debounce` (250 ms) para no
   * disparar una consulta por cada letra, y sus resultados caen ABAJO de los del
   * padrón cuando llegan. Si tarda o falla, `buscarGlobal` devuelve [] y el
   * buscador se ve igual que siempre: el que busca un interno no se entera de
   * que existe esta otra mitad hasta que le sirve.
   *
   * `pedido` numera cada búsqueda para descartar respuestas viejas que llegan
   * tarde (escribo «94», sale la consulta, sigo a «9401» y la primera contesta
   * después): sin esto, la lista parpadearía con resultados que ya no
   * corresponden a lo que hay escrito. */
  const [globales, setGlobales] = useState([]);
  const pedido = useRef(0);
  useEffect(() => {
    const q = texto.trim();
    if (q.length < MINIMO) {
      setGlobales([]);
      return;
    }
    const mio = ++pedido.current;
    const t = setTimeout(() => {
      buscarGlobal(q).then((res) => {
        /* Sólo la última búsqueda pinta: si mientras tanto salió otra, esta ya
           no manda y su respuesta se tira. */
        if (mio !== pedido.current) return;
        setGlobales(res);
      });
    }, 250);
    return () => clearTimeout(t);
  }, [texto]);

  /* Agrupadas por tipo, respetando el orden en que las trae la RPC (que ya
     ordena por relevancia de sector). */
  const gruposGlobales = useMemo(() => {
    const orden = [];
    const porTipo = {};
    for (const g of globales) {
      if (!porTipo[g.tipo]) {
        porTipo[g.tipo] = [];
        orden.push(g.tipo);
      }
      porTipo[g.tipo].push(g);
    }
    /* `lugar` es el puesto de cada renglón TAL COMO SE VE (agrupado), que no
       es el de la RPC: las flechas tienen que bajar en el orden de la
       pantalla. */
    let lugar = 0;
    return orden.map((tipo) => ({
      tipo,
      items: porTipo[tipo].map((g) => ({ ...g, lugar: lugar++ })),
    }));
  }, [globales]);

  useEffect(() => {
    const enTecla = (e) => {
      if (esAtajo(e)) {
        e.preventDefault();
        entrada.current?.focus();
        entrada.current?.select();
      }
      /* Escape cierra la lista Y LIMPIA: lo tipeado y lo marcado con las
       * flechas. Antes dejaba el texto, para no obligar a reescribirlo; Igna,
       * 02/10, lo pidió al revés —«que el Esc también limpie la selección»—:
       * Esc es «me voy de acá», y volver a la caja la encuentra vacía. */
      if (e.key === "Escape") {
        // Vaciar el texto ya devuelve lo marcado a cero (ver `elegido`).
        setAbierto(false);
        setTexto("");
      }
    };
    window.addEventListener("keydown", enTecla);
    return () => window.removeEventListener("keydown", enTecla);
  }, []);

  /* Un click afuera cierra. Va en `mousedown` y no en `blur` del input: con
   * `blur`, el click sobre un resultado cierra la lista antes de que el click
   * llegue, y el resultado no se puede tocar nunca. */
  useEffect(() => {
    const afuera = (e) => {
      if (caja.current && !caja.current.contains(e.target)) setAbierto(false);
    };
    document.addEventListener("mousedown", afuera);
    return () => document.removeEventListener("mousedown", afuera);
  }, []);

  const ir = (destino) => {
    setAbierto(false);
    setTexto("");
    navegar(destino);
  };

  /* LAS PANTALLAS (Spotlight: lo primero que encuentra son aplicaciones).
     Sale del menú en memoria, con la misma regla de permisos que el menú. */
  const pantallas = useMemo(() => pantallasQueCoinciden(texto, permisos), [texto, permisos]);

  const primero = r.unidades[0];

  /* ── CON EL TECLADO (Igna, 02/10: «debería poder usar el teclado para
   * elegir y enter para seleccionar») ──────────────────────────────────────
   *
   * Todos los resultados que llevan a algún lado, en el orden en que se ven:
   * pantallas, unidades, personas y lo operativo. ↑ y ↓ recorren ESA lista
   * —saltando de un grupo al otro sin enterarse— y Enter va al marcado. El
   * archivo de unidades vendidas no entra: no lleva a ningún lado.
   *
   * SIN TOCAR NADA, LO MARCADO ES LO QUE YA HACÍA ENTER: la primera unidad,
   * o la primera pantalla si no hay unidades ni personas. Así el Enter de
   * siempre no cambia, y ahora además se ve a dónde va antes de apretarlo.
   * Si no hay nada que Enter elegiría solo (una persona, un ticket), no se
   * marca nada hasta que se baja con la flecha: este buscador no elige por
   * vos. */
  const opciones = useMemo(() => {
    if (r.corto) return [];
    return [
      ...pantallas.map((p) => p.destino),
      ...r.unidades.map((u) => rutaDeUnidad(u.tipo, u.interno)),
      ...r.personas.map(
        (p) =>
          rutaDePersona(p.dni) ||
          `/padron/personas?buscar=${encodeURIComponent(p.nombre || "")}`,
      ),
      ...gruposGlobales.flatMap((grupo) => grupo.items.map((g) => g.ruta)),
    ];
  }, [r, pantallas, gruposGlobales]);
  const porDefecto = primero
    ? pantallas.length
    : !r.personas.length && pantallas.length
      ? 0
      : -1;
  // null = no se tocó ninguna flecha desde la última tecla: vale el de Enter.
  const [elegido, setElegido] = useState(null);
  const activo = elegido === null ? porDefecto : elegido;
  useEffect(() => setElegido(null), [texto]);

  // El marcado siempre a la vista: bajar con la flecha más allá del borde de
  // la lista la tiene que correr, si no se elige a ciegas.
  const lista = useRef(null);
  useEffect(() => {
    if (elegido === null) return;
    lista.current
      ?.querySelector(`[data-opcion="${elegido}"]`)
      ?.scrollIntoView?.({ block: "nearest" });
  }, [elegido]);

  const flechas = (e) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    if (!opciones.length) return;
    // Sin esto la flecha también lleva el cursor al principio o al final del
    // texto, y la lista se abre si estaba cerrada (Esc la cierra sin borrar).
    e.preventDefault();
    setAbierto(true);
    const n = opciones.length;
    setElegido(() => {
      if (e.key === "ArrowDown") return activo < 0 ? 0 : Math.min(activo + 1, n - 1);
      return activo <= 0 ? 0 : activo - 1;
    });
  };

  const enviar = (e) => {
    e.preventDefault();
    /* Enter va al MARCADO. Sin nada marcado no hace nada: navegar «a lo que
     * más se parezca» es exactamente lo que este buscador no hace. */
    if (activo >= 0 && opciones[activo]) ir(opciones[activo]);
  };

  /* Lo que cada botón necesita para verse marcado y para que un lector de
     pantalla sepa cuál es. `i` es su lugar en `opciones`. */
  const comoOpcion = (i) => ({
    id: `buscador-op-${i}`,
    role: "option",
    "data-opcion": i,
    "aria-selected": i === activo,
    className: "buscador-item" + (i === activo ? " buscador-item--activo" : ""),
  });
  const base = {
    unidades: pantallas.length,
    personas: pantallas.length + r.unidades.length,
    globales: pantallas.length + r.unidades.length + r.personas.length,
  };

  const mostrar = abierto && texto.trim().length > 0;
  useEffect(() => {
    if (mostrar && !seUso) setSeUso(true);
  }, [mostrar, seUso]);

  return (
    <div className="buscador" ref={caja}>
      <form className="buscador-caja" onSubmit={enviar} role="search">
        <span className="buscador-lupa" aria-hidden="true">
          ⌕
        </span>
        <input
          ref={entrada}
          type="search"
          value={texto}
          onChange={(e) => {
            setTexto(e.target.value);
            setAbierto(true);
          }}
          onFocus={() => setAbierto(true)}
          onKeyDown={flechas}
          aria-activedescendant={mostrar && activo >= 0 ? `buscador-op-${activo}` : undefined}
          aria-controls="buscador-lista"
          placeholder="Buscá lo que sea: interno, patente, apellido, vale…"
          aria-label="Buscar en el hub"
          autoComplete="off"
        />
        <kbd className="buscador-atajo" aria-hidden="true">
          Ctrl K
        </kbd>
      </form>

      {mostrar && (
        <div className="buscador-lista" role="listbox" id="buscador-lista" ref={lista}>
          {r.corto && (
            <p className="buscador-nada">
              Escribí al menos {MINIMO} caracteres.
            </p>
          )}

          {!r.corto && pantallas.length > 0 && (
            <>
              <div className="buscador-grupo">Pantallas</div>
              {pantallas.map((p, i) => (
                <button key={p.destino} type="button" {...comoOpcion(i)}
                        onClick={() => ir(p.destino)}>
                  <span className="buscador-item-txt">
                    <span className="buscador-item-tit">{p.label}</span>
                    <span className="buscador-item-meta">
                      {p.grupo}{p.sub ? ` · ${p.sub}` : ""}
                    </span>
                  </span>
                </button>
              ))}
            </>
          )}

          {!r.corto && !r.hay && (
            /* «No encontré» y no «no existe»: esto busca en los padrones que
               el hub tiene cargados, y decirlo evita que alguien concluya que
               la unidad no está en el sistema. Habla SÓLO del padrón a
               propósito: lo operativo (vales, siniestros, garantías) llega de
               la red y cae en su propio grupo abajo cuando hay algo, así que
               este mensaje no promete ni desmiente esa otra mitad. */
            <p className="buscador-nada">
              No encontré ninguna unidad ni persona con eso en los padrones.
              {" "}Lo demás del sistema aparece abajo cuando hay algo.
            </p>
          )}

          {r.unidades.length > 0 && (
            <>
              <div className="buscador-grupo">
                Unidades
                {/* CUANDO HAY DOS DEL MISMO NÚMERO SE DICE POR QUÉ. Sin esta
                    línea, ver el mismo 774 dos veces se lee como un error de
                    la pantalla y no como lo que es. */}
                {r.unidades.length > 1 &&
                  r.unidades[0].interno === r.unidades[1].interno && (
                    <em> · el mismo interno existe como tractor y como furgón</em>
                  )}
              </div>
              {r.unidades.map((u, i) => (
                <button
                  key={u.tipo + u.interno}
                  type="button"
                  {...comoOpcion(base.unidades + i)}
                  onClick={() => ir(rutaDeUnidad(u.tipo, u.interno))}
                >
                  <span className="plate-chip">
                    {u.tipo === "furgon" ? `F${u.interno}` : u.interno}
                  </span>
                  <span className="buscador-item-txt">
                    <span className="buscador-item-tit">
                      {u.tipo === "tractor" ? "Tractor" : "Furgón"} {u.interno}
                      <Empresa valor={u.empresa} />
                    </span>
                    <span className="buscador-item-meta">
                      {/* SIN CHAPA NO ES LO MISMO QUE NO ENCONTRARLA: son 64
                          tractores, y un guion ahí manda a corregir un interno
                          bien escrito. La que está va como chapa Mercosur
                          (Igna, 03/10: «quedan re facheras»): al lado del
                          interno de la izquierda, que también va en un
                          recuadro, se distingue cuál es cuál sin leerlos. */}
                      {u.dominio ? (
                        <Patente dominio={u.dominio} />
                      ) : (
                        "sin patente cargada"
                      )}
                      {u.marca ? ` · ${u.marca}` : ""} · {POR[u.por]}
                    </span>
                    {polizas && u.dominio && (() => {
                      const sg = lineaSeguro(
                        polizas.get(dominioPoliza(u.dominio)) ?? null,
                      );
                      return (
                        <span
                          className={
                            "buscador-item-seguro" +
                            (sg.aviso ? " buscador-item-seguro--aviso" : "") +
                            (sg.neutro ? " tenue" : "")
                          }
                        >
                          {sg.neutro ? "Póliza sin cargar" : sg.texto}
                        </span>
                      );
                    })()}
                  </span>
                  {u.activo === false && (
                    <span className="pill gris">de baja</span>
                  )}
                </button>
              ))}
              {r.demas.unidades > 0 && (
                /* Lo que no entra se CUENTA. Una lista recortada en silencio
                   se lee como la lista completa. */
                <p className="buscador-mas">
                  y {r.demas.unidades} unidad
                  {r.demas.unidades === 1 ? "" : "es"} más — afiná la búsqueda
                </p>
              )}
            </>
          )}

          {r.personas.length > 0 && (
            <>
              <div className="buscador-grupo">Personas</div>
              {r.personas.map((p, i) => {
                /* ── SIN DNI NO HAY FICHA, Y EL RESULTADO LO DICE ─────────
                 *
                 * Igna, 15/09, buscando a un alta recién hecha: *«busco a
                 * alguien ahí y no hace nada»*. Era literal: la ficha se
                 * direcciona por DNI (`/persona/:dni`), esa persona se cargó
                 * sin DNI, `rutaDePersona` devolvía `null` y el click cerraba
                 * el buscador sin ir a ningún lado. El resultado se ofrecía
                 * igual, idéntico a los que sí llevan.
                 *
                 * Son 2 de 2.478 —los dos últimos legajos, 2047 y 2048— así
                 * que es raro, y por eso es peor: pasa una vez cada tanto y
                 * el que lo sufre concluye que el buscador está roto.
                 *
                 * NO SE ESCONDE EL RESULTADO. La persona existe y encontrarla
                 * es la respuesta correcta a lo que se buscó; lo que no existe
                 * es su ficha. Así que se dice qué falta y se lleva al padrón
                 * con el nombre puesto, que es DONDE SE COMPLETA — el que
                 * buscó queda a un campo de arreglarlo, en vez de frente a
                 * una pantalla que no reaccionó. */
                const aLaFicha = rutaDePersona(p.dni);
                const destino =
                  aLaFicha ||
                  `/padron/personas?buscar=${encodeURIComponent(p.nombre || "")}`;
                return (
                  <button
                    key={p.dni || `legajo-${p.legajo}` || p.nombre}
                    type="button"
                    {...comoOpcion(base.personas + i)}
                    /* A SU FICHA. Antes iba al padrón con la búsqueda puesta
                       —`/padron/personas?buscar=`— porque la persona no tenía
                       pantalla propia: un resultado que deja al que buscó
                       leyendo una fila de veinte columnas. Ahora la tiene, y
                       el padrón quedó como la salida del que no tiene DNI. */
                    onClick={() => ir(destino)}
                  >
                    <span className="buscador-item-txt">
                      <span className="buscador-item-tit">
                        {p.nombre}
                        <Empresa valor={p.empresa} />
                      </span>
                      <span className="buscador-item-meta">
                        {aLaFicha ? `DNI ${p.dni}` : "sin DNI — se carga en el padrón"}
                        {p.legajo ? ` · legajo ${p.legajo}` : ""} · {POR[p.por]}
                        {p.baja ? " · ya no trabaja" : ""}
                      </span>
                    </span>
                  </button>
                );
              })}
              {r.demas.personas > 0 && (
                <p className="buscador-mas">
                  y {r.demas.personas} persona
                  {r.demas.personas === 1 ? "" : "s"} más — afiná la búsqueda
                </p>
              )}
            </>
          )}

          {/* EL ARCHIVO VA ÚLTIMO Y NO SE PUEDE TOCAR.
              Último porque lo que trabaja va primero: el que busca el 92 casi
              siempre busca el 92 de hoy. Y sin `onClick` porque no hay a dónde
              ir — estas unidades no tienen ficha, y un botón que no lleva a
              ningún lado es peor que un renglón que no lo parece.
              La fila dice lo que decía la planilla y nada más: es todo lo que
              queda de ese camión. */}
          {r.historicas.length > 0 && (
            <>
              <div className="buscador-grupo">Ya no son de la flota</div>
              {r.historicas.map((h) => (
                <div key={h.dominio} className="buscador-item buscador-item--archivo">
                  <span className="buscador-item-txt">
                    <span className="buscador-item-tit">
                      {h.interno ? `Interno ${h.interno}` : "Sin interno"} ·{" "}
                      <Patente dominio={h.dominio} />
                    </span>
                    <span className="buscador-item-meta">
                      {[
                        [h.marca, h.modelo].filter(Boolean).join(" "),
                        h.anio || null,
                        h.empresa || null,
                        h.estado_planilla || null,
                        POR[h.por],
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </span>
                </div>
              ))}
              {r.demas.historicas > 0 && (
                <p className="buscador-mas">
                  y {r.demas.historicas} más en el archivo
                </p>
              )}
              <p className="buscador-mas">
                Salen de la planilla de carpeta. No están en el padrón.
              </p>
            </>
          )}

          {/* LO OPERATIVO (vales, siniestros, garantías): la mitad general, que
              llega de la red y cae acá abajo. Cada grupo es su tipo tal como lo
              nombra la RPC, y cada renglón lleva a la pantalla del sector con su
              `ruta`. Como el permiso lo pone el servidor, acá aparece sólo lo
              que el que busca ya puede ver. */}
          {gruposGlobales.map((grupo) => (
            <div key={grupo.tipo}>
              <div className="buscador-grupo">{grupo.tipo}</div>
              {grupo.items.map((g, i) => (
                <button
                  key={grupo.tipo + i}
                  type="button"
                  {...comoOpcion(base.globales + g.lugar)}
                  onClick={() => ir(g.ruta)}
                >
                  <span className="buscador-item-txt">
                    <span className="buscador-item-tit">{g.titulo}</span>
                    {g.subtitulo && (
                      <span className="buscador-item-meta">{g.subtitulo}</span>
                    )}
                  </span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Buscador;
